import React, { useEffect, useMemo, useState } from 'react';
import { KatButton } from '@amzn/katal-react';
import { AiInsightsService, TrackerAiSummaryResponse } from '../services/Ai/AiInsightsService';
import './TrackerAiInsights.scss';

interface TrackerAiInsightsProps {
    stage: string;
    realm: string;
    trackerData: {
        created: number;
        approved: number;
        declined: number;
        pending: number;
        batched: number;
        unbatched: number;
        unstarted: number;
        withinSla: number;
        totalRefundsCreated: number;
        refundsPending: number;
        overSla: number;
        timeWindow: string;
        clientId?: string;
        metadata?: Record<string, unknown>;
    } | null;
}

const TrackerAiInsights: React.FC<TrackerAiInsightsProps> = ({ stage, realm, trackerData }) => {
    const [insights, setInsights] = useState<TrackerAiSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const trackerContextKey = useMemo(() => {
        if (!trackerData) {
            return `${stage}:${realm}:empty`;
        }

        return JSON.stringify({
            stage,
            realm,
            generatedAt: trackerData.metadata?.generatedAt,
            timeWindow: trackerData.timeWindow,
            clientId: trackerData.clientId,
            totals: {
                totalRefundsCreated: trackerData.totalRefundsCreated,
                refundsPending: trackerData.refundsPending,
                overSla: trackerData.overSla,
            },
        });
    }, [stage, realm, trackerData]);

    useEffect(() => {
        setInsights(null);
        setError(null);
    }, [trackerContextKey]);

    const handleGenerateInsights = async () => {
        if (!trackerData) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await AiInsightsService.getTrackerSummary(stage, realm, trackerData);
            setInsights(response);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to generate AI insights');
            setInsights(null);
        } finally {
            setLoading(false);
        }
    };

    const buttonLabel = loading
        ? 'Generating...'
        : insights
            ? 'Refresh AI Insights'
            : 'Generate AI Insights';

    return (
        <div className="tracker-ai-insights">
            <div className="tracker-ai-insights__header">
                <div className="tracker-ai-insights__intro">
                    <div className="tracker-ai-insights__eyebrow">Sherlock AI</div>
                    <h2 className="tracker-ai-insights__title">Tracker Intelligence</h2>
                    <p className="tracker-ai-insights__subtitle">
                        Ask Sherlock AI to summarize the current tracker state and highlight the next operational checks.
                    </p>
                </div>
                <KatButton
                    variant="primary"
                    label={buttonLabel}
                    onClick={handleGenerateInsights}
                    disabled={loading || !trackerData}
                />
            </div>

            {!trackerData && (
                <div className="tracker-ai-insights__empty">Load tracker data to generate a grounded summary.</div>
            )}

            {error && (
                <div className="tracker-ai-insights__error">{error}</div>
            )}

            {insights && (
                <div className="tracker-ai-insights__content">
                    <div className="tracker-ai-insights__summary">{insights.summary}</div>

                    <InsightList title="Signals" items={insights.signals} />
                    <InsightList title="Recommended Actions" items={insights.recommendedActions} />
                    <InsightList title="Caveats" items={insights.caveats} />

                    {(insights.modelId || insights.generatedAt) && (
                        <div className="tracker-ai-insights__meta">
                            {insights.modelId && <span>Model: {insights.modelId}</span>}
                            {insights.generatedAt && <span>Generated: {insights.generatedAt}</span>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const InsightList: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
    if (!items || items.length === 0) {
        return null;
    }

    return (
        <div className="tracker-ai-insights__list-block">
            <h3>{title}</h3>
            <ul>
                {items.map((item) => (
                    <li key={`${title}-${item}`}>{item}</li>
                ))}
            </ul>
        </div>
    );
};

export default TrackerAiInsights;
